import { LOCALE_TAGS, microsToString } from '@aitoken/contracts';
import { commonT } from '@/i18n/messages/common';
import { getLocale, onLocaleChange } from '@/i18n/runtime';

let displayTimeZone = 'Asia/Shanghai';
/** 日期 / 数字格式跟随界面语言（不是站点语言） */
let displayLocale = LOCALE_TAGS[getLocale()];
onLocaleChange((l) => {
  displayLocale = LOCALE_TAGS[l];
});

/** 由站点信息设置展示时区（cn 站默认 Asia/Shanghai）。语言参数保留兼容，已不使用。 */
export function configureDisplay(timeZone: string, _locale?: string) {
  displayTimeZone = timeZone || 'Asia/Shanghai';
}

export function getDisplayLocale() {
  return displayLocale;
}

export function getDisplayTimeZone() {
  return displayTimeZone;
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();
function dtf(key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const cacheKey = `${displayLocale}|${displayTimeZone}|${key}`;
  let f = dtfCache.get(cacheKey);
  if (!f) {
    f = new Intl.DateTimeFormat(displayLocale, { timeZone: displayTimeZone, hour12: false, ...options });
    dtfCache.set(cacheKey, f);
  }
  return f;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return dtf('dt', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(d);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return dtf('d', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

export function formatShortDay(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dtf('md', {
    month: 'numeric',
    day: 'numeric',
    timeZone: iso.length === 10 ? 'UTC' : displayTimeZone,
  }).format(d);
}

const rtfCache = new Map<string, Intl.RelativeTimeFormat>();
function rtfFor(): Intl.RelativeTimeFormat {
  let f = rtfCache.get(displayLocale);
  if (!f) rtfCache.set(displayLocale, (f = new Intl.RelativeTimeFormat(displayLocale, { numeric: 'auto' })));
  return f;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return commonT('time.never');
  const rtf = rtfFor();
  const diffSec = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(diffSec, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSec / 86400), 'day');
  return formatDate(iso);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} ${units[i]}`;
}

/** 毫秒 → “1 分 05 秒”（按界面语言） */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return commonT('duration.seconds', { s: totalSec });
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m < 60) return commonT('duration.minutes', { m, s: String(s).padStart(2, '0') });
  const h = Math.floor(m / 60);
  return commonT('duration.hours', { h, m: m % 60 });
}

export function elapsedMs(startIso: string, endIso: string | null): number {
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  return Math.max(0, end - new Date(startIso).getTime());
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat(displayLocale).format(n);
}

export function microsToMoney(micros: bigint): string {
  return microsToString(micros);
}

/** <input type="date"> 值（站点时区的自然日）→ ISO 时间边界。 */
export function dayStartIso(day: string): string {
  return new Date(`${day}T00:00:00${offsetFor(day)}`).toISOString();
}
export function dayEndIso(day: string): string {
  return new Date(`${day}T23:59:59.999${offsetFor(day)}`).toISOString();
}

function offsetFor(day: string): string {
  // 计算站点时区在该日的 UTC 偏移（如 +08:00）
  const probe = new Date(`${day}T12:00:00Z`);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: displayTimeZone,
    timeZoneName: 'longOffset',
  }).formatToParts(probe);
  const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const m = /GMT([+-]\d{2}:\d{2})/.exec(name);
  return m?.[1] ?? 'Z';
}

export function todayInZone(offsetDays = 0): string {
  const d = new Date(Date.now() + offsetDays * 86400_000);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: displayTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  return parts; // en-CA → YYYY-MM-DD
}
